import type { Texture } from '@anov/3d-core'
import { ClampToEdgeWrapping, Color, DoubleSide, Mesh, MeshBasicMaterial, QuadraticBezierCurve3, RepeatWrapping, TextureLoader, TubeGeometry, Vector3, use } from '@anov/3d-core'
import ObjectLayer from '../../Base/ObjectLayer'
import type { LineOption } from '../type'
import { IMG } from './index'

class CurveLine extends ObjectLayer {
  private _startPoint: Vector3
  private _endPoint: Vector3
  private _width: number
  private _color: string
  private _curve: number
  private _tiling: number
  private _duration: number
  private _loop: boolean
  private _delay: number

  private _material: MeshBasicMaterial
  private _mesh: Mesh
  private _readyPromise: Promise<boolean>
  private _cancelAnimation: Function

  public get width(): number {
    return this._width
  }

  public set width(value: number) {
    this._width = value
    this.destroy()
    this.init()
  }

  public get color(): string {
    return this._color
  }

  public set color(value: string) {
    this._color = value
    this._material.color = new Color(this._color)
  }

  public get curve(): number {
    return this._curve
  }

  public set curve(value: number) {
    this._curve = value
    this.destroy()
    this.init()
  }

  public get duration(): number {
    if (this._duration <= 0)
      return 1
    return this._duration
  }

  public set duration(value: number) {
    this._duration = value
  }

  public get tiling(): number {
    if (this._tiling <= 0)
      return 0
    return this._tiling
  }

  public set tiling(value: number) {
    this._tiling = value
  }

  public get loop(): boolean {
    return this._loop
  }

  public set loop(value: boolean) {
    this._loop = value
  }

  public get delay(): number {
    return this._delay
  }

  public set delay(value: number) {
    this._delay = value
  }

  public get mesh(): Mesh | null {
    return this._mesh
  }

  public get ready() {
    return this._readyPromise
  }

  constructor(params: LineOption, sceneControl?: any) {
    super(sceneControl)
    const { startPoint, endPoint, width = 0.2, color = '#28FFD9', curve = 0.6, tiling = 1, duration = 1, loop = true, delay = 0 } = params
    this._startPoint = new Vector3().fromArray(startPoint)
    this._endPoint = new Vector3().fromArray(endPoint)
    this._width = width
    this._color = color
    this._curve = curve
    this._tiling = tiling
    this._duration = duration
    this._loop = loop
    this._delay = delay

    this._readyPromise = this.init()
  }

  private async init() {
    const curve = this.getCurve()

    const texture = await this.loadTexture(IMG)
    const tube = new TubeGeometry(curve, Math.floor(curve.getLength() * 200), this.width, 2, false)
    this._material = new MeshBasicMaterial({
      color: this.color,
      map: texture,
      side: DoubleSide,
      transparent: true, // 透明
    })
    this._mesh = new Mesh(tube, this._material)
    this._mesh.renderOrder = 10
    this.add(this._mesh)

    // 动画
    this.setupAnimation(texture)

    return true
  }

  private async loadTexture(path: string): Promise<Texture> {
    const textLoader = new TextureLoader()
    return new Promise((resolve) => {
      const texture = textLoader.load(path, () => {
        // 纹理加载完成后设置属性
        // texture.center.set(0.5, 0.5) // 设置旋转中心为纹理中心
        // texture.rotation = Math.PI / 2 // 旋转90度
        texture.repeat.set(this.tiling, 2)
        texture.offset.set(0, 0)
        texture.wrapS = RepeatWrapping
        texture.wrapT = ClampToEdgeWrapping
        resolve(texture)
      })
    })
  }

  private setupAnimation(texture: Texture) {
    let isAnimating = true
    let totalOffset = 0
    let lastTime = performance.now()
    let isDelaying = false
    let delayStartTime = 0

    this._cancelAnimation = use.useframe(this.sceneControl, () => {
      const currentTime = performance.now()
      // 处理延迟状态
      if (isDelaying) {
      // 检查是否延迟时间已结束
        if ((currentTime - delayStartTime) / 1000 >= this.delay) {
          isDelaying = false
          // 重置偏移量计数和动画状态
          totalOffset = 0
          isAnimating = true
          lastTime = currentTime
        }
        return // 在延迟期间不进行动画
      }

      // 如果不在动画中，直接返回
      if (!isAnimating)
        return

      const deltaTime = (currentTime - lastTime)
      lastTime = currentTime

      // 使用 deltaTime 进行动画计算
      const offsetPerFrame = (deltaTime / (this.duration / this.tiling * 1000))
      // 更新总偏移量
      totalOffset += offsetPerFrame

      // 更新纹理偏移
      texture.offset.x -= offsetPerFrame

      // 检查是否完成一次完整的UV移动
      if (totalOffset >= this.tiling) {
      // 重置偏移量以确保精确的位置
        totalOffset = 0
        texture.offset.x = 0

        // 如果不循环，则结束动画
        if (!this.loop) {
          isAnimating = false
        }
        // 如果有延迟且需要循环，则开始延迟
        else if (this.delay > 0) {
          isDelaying = true
          delayStartTime = currentTime
          isAnimating = false // 在延迟期间暂停动画
        }
      }
    })
  }

  private getCurve() {
    // 计算起点和终点的中点
    const midPoint = new Vector3(
      (this._startPoint.x + this._endPoint.x) / 2,
      (this._startPoint.y + this._endPoint.y) / 2,
      (this._startPoint.z + this._endPoint.z) / 2,
    )

    // 计算起点和终点的方向向量
    const direction = new Vector3().subVectors(this._endPoint, this._startPoint)

    // 计算距离，用于确定弧的高度
    const distance = direction.length()

    // 使用arcFactor控制弧线的高度
    const height = distance * this.curve

    // 计算控制点 - 在中点上方
    const controlPoint = new Vector3()
      .copy(midPoint)
      .addScaledVector(new Vector3(0, 1, 0), height)

    // 创建曲线
    const curve = new QuadraticBezierCurve3(
      this._startPoint,
      controlPoint,
      this._endPoint,
    )
    return curve
  }

  destroy() {
    this._cancelAnimation && this._cancelAnimation()
    // 释放几何体资源
    if (this._mesh) {
      if (this._mesh.geometry)
        this._mesh.geometry.dispose()

      // 释放材质资源
      if (this._mesh.material) {
        // 释放材质
        if (Array.isArray(this._mesh.material))
          this._mesh.material.forEach(material => material.dispose())
        else
          this._mesh.material.dispose()
      }

      // 从场景中移除网格
      this.remove(this._mesh)
      this._mesh = null
    }
  }
}

export default CurveLine